import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { OnboardingData } from "@/pages/onboarding/Onboarding";
import {
  ArrowRight,
  ArrowLeft,
  Users,
  UserMinus,
  CalendarX2,
  TrendingUp,
  Loader2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { fetchAllSupabaseRows } from "@/lib/supabasePagination";

// Slide 4. "Value metrics" (shown after the contact import on the Connectors
// slide, immediately before the Plan slide).
//
// Quantifies the revenue Dibs can recover from the owner's OWN imported contacts
// the "aha moment" that leads into plan selection. Display-only: contacts are
// imported upstream (connecting a channel / booking integration on the Connectors
// slide), not here. All figures are computed live from the business's own data
// (RLS-scoped to this owner), so the numbers are real, not stubbed.
//
// Data model notes (resolved from the schema during build):
//   • `customers` = imported contacts (business_id scoped). Metrics #1 + #2.
//     `last_booking_at` (owner-declared, from the contacts CSV) marks a contact
//     active even when no booking rows exist yet.
//   • `bookings` have no business_id; they're scoped via listing_id → listings.
//     A contact's booking history is matched by customer_id OR lowercased email
//     OR phone. Bookings arrive via the onboarding/dashboard CSV import or a
//     booking integration.
//   • "Cancelled within 24h of start": status='cancelled' and the cancellation
//     time falls within 24h of the slot start. `cancelled_at` (from the CSV) is
//     authoritative; `updated_at` is the legacy fallback (equals import time
//     for imported rows, so it rarely matches on its own).

// Tunable revenue assumptions (owner-provided placeholders). Named constants so
// they're easy to tweak. Consider deriving from per-business avg ticket later.
const REVENUE_PER_DORMANT_CONTACT = 200; // $ a re-engaged lapsed customer is worth
const REVENUE_PER_LATE_CANCEL = 100; // $ lost per slot emptied by a late cancel
const DORMANT_DAYS = 60; // "haven't booked in the past N days"
const LOOKBACK_DAYS = 60; // window for counting late cancels
const LATE_CANCEL_WINDOW_HOURS = 24; // "within 24h of start time"

interface Props {
  data: OnboardingData;
  updateData: (fields: Partial<OnboardingData>) => void;
  nextStep: () => void | Promise<void>;
  prevStep: () => void;
  isFinalStep?: boolean;
  isSubmitting?: boolean;
}

interface Metrics {
  contacts: number;
  dormantContacts: number;
  lateCancels: number;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const norm = (v: string | null | undefined) => (v ?? "").trim().toLowerCase();

// Parse a booking slot (date + optional "HH:mm" text) into a Date.
const slotStartOf = (date: string | null, time: string | null): Date | null => {
  if (!date) return null;
  const t = (time ?? "").trim();
  const iso = `${date}T${/^\d{1,2}:\d{2}/.test(t) ? t.slice(0, 5) : "00:00"}:00`;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

async function computeMetrics(businessId: string): Promise<Metrics> {
  const now = Date.now();
  const dormantCutoff = now - DORMANT_DAYS * MS_PER_DAY;
  const lookbackCutoff = now - LOOKBACK_DAYS * MS_PER_DAY;

  // 1. Imported contacts.
  const contactRows = await fetchAllSupabaseRows(async (from, to) => {
    const { data, error } = await supabase
      .from("customers")
      .select("customer_id, email, phone, last_booking_at")
      .eq("business_id", businessId)
      .order("customer_id", { ascending: true })
      .range(from, to);
    return { data, error };
  });

  // 2. This business's listings → ids, to scope bookings (no business_id column).
  const listings = await fetchAllSupabaseRows(async (from, to) => {
    const { data, error } = await supabase
      .from("listings")
      .select("listing_id")
      .eq("business_id", businessId)
      .order("listing_id", { ascending: true })
      .range(from, to);
    return { data, error };
  });
  const listingIds = listings
    .map((l) => l.listing_id)
    .filter(Boolean) as string[];

  let bookingRows: {
    customer_id: string | null;
    customer_email: string | null;
    customer_phone: string | null;
    booking_date: string | null;
    booking_time: string | null;
    status: string | null;
    cancelled_at: string | null;
    updated_at: string | null;
  }[] = [];
  if (listingIds.length > 0) {
    bookingRows = await fetchAllSupabaseRows(async (from, to) => {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          "booking_id, customer_id, customer_email, customer_phone, booking_date, booking_time, status, cancelled_at, updated_at",
        )
        .in("listing_id", listingIds)
        .order("booking_id", { ascending: true })
        .range(from, to);
      return {
        data: (data ?? []).map(({ booking_id: _bookingId, ...booking }) => booking),
        error,
      };
    });
  }

  // Index each contact's most-recent booking slot (match by id / email / phone).
  const latestBookingByKey = new Map<string, number>();
  const noteLatest = (key: string, ts: number) => {
    if (!key) return;
    const prev = latestBookingByKey.get(key);
    if (prev === undefined || ts > prev) latestBookingByKey.set(key, ts);
  };

  let lateCancels = 0;
  for (const b of bookingRows) {
    const slot = slotStartOf(b.booking_date, b.booking_time);
    if (slot) {
      const ts = slot.getTime();
      if (b.customer_id) noteLatest(`id:${b.customer_id}`, ts);
      if (b.customer_email) noteLatest(`e:${norm(b.customer_email)}`, ts);
      if (b.customer_phone) noteLatest(`p:${norm(b.customer_phone)}`, ts);
    }

    // Metric #3. Late cancels: cancelled within 24h of a slot that fell in the
    // last 60 days. cancelled_at (CSV-supplied) is authoritative; updated_at is
    // the legacy fallback for rows cancelled in-app before the column existed.
    const cancelSource = b.cancelled_at ?? b.updated_at;
    if ((b.status ?? "").toLowerCase() === "cancelled" && slot && cancelSource) {
      const slotTs = slot.getTime();
      const cancelTs = new Date(cancelSource).getTime();
      const slotInWindow = slotTs >= lookbackCutoff && slotTs <= now;
      const cancelledNearStart =
        !isNaN(cancelTs) &&
        Math.abs(slotTs - cancelTs) <= LATE_CANCEL_WINDOW_HOURS * 60 * 60 * 1000;
      if (slotInWindow && cancelledNearStart) lateCancels += 1;
    }
  }

  // Metric #2. Dormant contacts: no booking in the last 60 days (incl. never).
  // A contact's most recent activity is the max of their booking rows and the
  // owner-declared last_booking_at from the contacts CSV.
  let dormantContacts = 0;
  for (const c of contactRows) {
    const keys = [
      c.customer_id ? `id:${c.customer_id}` : "",
      c.email ? `e:${norm(c.email)}` : "",
      c.phone ? `p:${norm(c.phone)}` : "",
    ].filter(Boolean);
    let latest: number | undefined;
    for (const k of keys) {
      const ts = latestBookingByKey.get(k);
      if (ts !== undefined && (latest === undefined || ts > latest)) latest = ts;
    }
    if (c.last_booking_at) {
      const declared = new Date(c.last_booking_at).getTime();
      if (!isNaN(declared) && (latest === undefined || declared > latest)) {
        latest = declared;
      }
    }
    if (latest === undefined || latest < dormantCutoff) dormantContacts += 1;
  }

  return { contacts: contactRows.length, dormantContacts, lateCancels };
}

const money = (n: number) =>
  `$${n.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const ImportValueMetricsStep = ({ nextStep, prevStep, isFinalStep = false, isSubmitting = false }: Props) => {
  const { businessId } = useAuth();
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!businessId) {
      setMetrics({ contacts: 0, dormantContacts: 0, lateCancels: 0 });
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      setMetrics(await computeMetrics(businessId));
    } catch (e) {
      console.error("[onboarding] failed to compute value metrics:", e);
      setMetrics(null);
      setLoadError(e instanceof Error ? e.message : "Value metrics could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    load();
  }, [load]);

  const dormantValue = (metrics?.dormantContacts ?? 0) * REVENUE_PER_DORMANT_CONTACT;
  const lateCancelValue = (metrics?.lateCancels ?? 0) * REVENUE_PER_LATE_CANCEL;
  const total = dormantValue + lateCancelValue;
  const hasData =
    !!metrics &&
    (metrics.contacts > 0 || metrics.dormantContacts > 0 || metrics.lateCancels > 0);

  if (loading) {
    return (
      <div className="flex min-h-full flex-1 flex-col">
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-sm">Crunching your imported data…</p>
        </div>
        <div className="mt-auto flex justify-between">
          <Button type="button" variant="outline" size="sm" onClick={prevStep}>
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Back
          </Button>
          <Button type="button" size="sm" disabled>
            Complete setup <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex min-h-full flex-1 flex-col">
        <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-8 text-center">
          <AlertCircle className="h-7 w-7 text-destructive" />
          <div>
            <p className="text-sm font-semibold text-foreground">Your value metrics could not be loaded</p>
            <p className="mt-1 text-xs text-muted-foreground">No zero values have been assumed. Retry the database read before completing setup.</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => void load()}>
            <RefreshCw className="mr-1.5 h-4 w-4" />
            Retry metrics
          </Button>
        </div>
        <div className="mt-auto flex justify-between pt-6">
          <Button type="button" variant="outline" size="sm" onClick={prevStep}>
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Back
          </Button>
          <Button type="button" size="sm" disabled>
            Complete setup <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="space-y-6">
      {/* Hero — total revenue sitting in gaps. */}
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-6 text-center">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-primary/10">
          <TrendingUp className="h-5 w-5 text-primary" />
        </div>
        <p className="text-3xl font-bold tracking-tight text-foreground">
          ~{money(total)}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          sitting in revenue gaps right now. Dibs recovers it automatically —
          usually paid back by a single saved customer.
        </p>
      </div>

      {/* Breakdown. */}
      <div className="space-y-3">
        <MetricRow
          icon={Users}
          headline={
            <>
              <strong>{metrics?.contacts ?? 0}</strong> contacts imported
            </>
          }
          sub="The customers Dibs now has to work with."
        />
        <MetricRow
          icon={UserMinus}
          headline={
            <>
              <strong>{metrics?.dormantContacts ?? 0}</strong> contacts haven't
              booked in the past {DORMANT_DAYS} days
            </>
          }
          sub={
            <>
              Dibs helps you re-engage them to recover over{" "}
              <strong className="text-foreground">{money(dormantValue)}</strong>{" "}
              worth of revenue.
            </>
          }
        />
        <MetricRow
          icon={CalendarX2}
          headline={
            <>
              <strong>{metrics?.lateCancels ?? 0}</strong> bookings cancelled
              within 24h of start time in the past {LOOKBACK_DAYS} days
            </>
          }
          sub={
            <>
              Slots that likely went empty — that's{" "}
              <strong className="text-foreground">
                {money(lateCancelValue)}
              </strong>{" "}
              lost. Dibs fills them with auto-confirmations and a smart waitlist.
            </>
          }
        />
      </div>

      {/* Empty state — contacts are imported on the previous (Connectors) slide
          by connecting a channel or booking integration; nudge back there. */}
        {!hasData && (
        <div className="flex flex-col items-start gap-2 rounded-lg border border-dashed border-border bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            No contacts imported yet. Connect a channel or booking tool on the
            previous step and they'll sync here automatically.
          </p>
          <Button type="button" variant="outline" size="sm" onClick={prevStep}>
            <ArrowLeft className="h-4 w-4" />
            Connect &amp; import
          </Button>
        </div>
        )}
      </div>

      <div className="mt-auto flex justify-between">
        <Button type="button" variant="outline" size="sm" onClick={prevStep} disabled={isSubmitting}>
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back
        </Button>
        <Button type="button" size="sm" onClick={() => void nextStep()} disabled={isSubmitting}>
          {isFinalStep ? "Complete setup" : "Continue"} <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const MetricRow = ({
  icon: Icon,
  headline,
  sub,
}: {
  icon: typeof Users;
  headline: React.ReactNode;
  sub: React.ReactNode;
}) => (
  <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
      <Icon className="h-4 w-4 text-primary" />
    </span>
    <div className="min-w-0">
      <p className="text-sm leading-snug text-foreground">{headline}</p>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{sub}</p>
    </div>
  </div>
);

export default ImportValueMetricsStep;
